
import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy';

import { useEmailWithFailover } from './useEmailWithFailover';

interface AuthRateLimitResult {
  allowed: boolean;
  reason?: string;
  locked_until?: string;
}

export const useSecureAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authAttempts, setAuthAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<number>(0);

  const { sendSignupEmail: sendFailoverSignupEmail, sendPasswordResetEmail: sendFailoverPasswordResetEmail } = useEmailWithFailover();

  const checkRateLimit = useCallback((email: string): AuthRateLimitResult => {
    const now = Date.now();
    const rateLimitWindow = 60000; // 1 minute
    const maxAttempts = 5;
    
    // Simple client-side rate limiting
    if (authAttempts >= maxAttempts && (now - lastAttemptTime) < rateLimitWindow) {
      const lockedUntil = new Date(lastAttemptTime + rateLimitWindow);
      return {
        allowed: false,
        reason: 'rate_limited',
        locked_until: lockedUntil.toISOString()
      };
    }
    
    // Reset attempts if window has passed
    if ((now - lastAttemptTime) >= rateLimitWindow) {
      setAuthAttempts(0);
    }
    
    return { allowed: true };
  }, [authAttempts, lastAttemptTime]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  // Import password policy hook at the top of the component
  const { validatePassword: validatePasswordPolicy } = usePasswordPolicy();
  
  const validatePassword = validatePasswordPolicy;

  const resetMonthlyCredits = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan_type, monthly_limit, updated_at')
        .eq('id', userId)
        .single();

      if (profile) {
        const lastUpdate = new Date(profile.updated_at);
        const now = new Date();
        const isNewMonth = lastUpdate.getMonth() !== now.getMonth() || lastUpdate.getFullYear() !== now.getFullYear();

        if (isNewMonth) {
          let creditsToReset = profile.monthly_limit;
          
          await supabase
            .from('profiles')
            .update({
              credits_remaining: creditsToReset,
              image_generations_used: 0,
              updated_at: now.toISOString()
            })
            .eq('id', userId);

          console.log(`Monthly credits reset for user ${userId}: ${creditsToReset} credits`);
        }
      }
    } catch (error) {
      console.error('Error resetting monthly credits:', error);
    }
  };

  // Ensure a profile row exists for the authenticated user
  const ensureUserProfile = async (userId: string, email?: string, fullName?: string) => {
    try {
      const { data: existing, error: selectError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        console.warn('Profile select error:', selectError);
      }

      if (!existing) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: email || '',
            full_name: (fullName || '').trim(),
            plan_type: 'free',
            credits_remaining: 50,
            monthly_limit: 250,
            image_generations_used: 0
          });
        if (insertError) {
          console.warn('Profile insert failed:', insertError);
        } else {
          console.log('Profile created for user:', userId);
        }
      }
    } catch (e) {
      console.warn('ensureUserProfile failed:', e);
    }
  };

  const secureSignUp = async (email: string, password: string, fullName?: string) => {
    console.log('=== SIGNUP FUNCTION CALLED ===');
    console.log('Email:', email);
    
    // Input sanitization and validation
    const sanitizedEmail = email.toLowerCase().trim();
    
    if (!validateEmail(sanitizedEmail)) {
      // Log security event for invalid email attempts with enhanced monitoring
      try {
        await supabase.rpc('log_security_event_enhanced', {
          event_type: 'invalid_email_signup',
          event_data: { email_format: 'invalid' },
          severity: 'low'
        });
      } catch (e) {
        console.warn('Failed to log enhanced security event:', e);
      }
      return { error: { message: 'Invalid email format' } };
    }

    // Check if email is in the allowlist OR is a legitimate domain
    try {
      const { data: isAllowed, error: allowlistError } = await supabase.rpc('is_email_allowed', {
        check_email: sanitizedEmail
      });

      if (allowlistError) {
        console.error('Error checking email allowlist:', allowlistError);
      }

      // List of legitimate email providers (same as Auth.tsx)
      const legitimateEmailDomains = [
        'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
        'aol.com', 'protonmail.com', 'mail.com', 'zoho.com', 'yandex.com',
        'live.com', 'msn.com', 'rediffmail.com', 'tutanota.com', 'fastmail.com',
        'gmx.com', 'mail.ru', 'qq.com', '163.com', 'sina.com'
      ];

      const domain = sanitizedEmail.split('@')[1]?.toLowerCase();
      const isLegitimate = legitimateEmailDomains.includes(domain || '');

      // Allow if email is on allowlist OR domain is legitimate
      if (!isAllowed && !isLegitimate) {
        console.log('Email not authorized:', sanitizedEmail);
        return { error: { message: 'This email address is not authorized to access the platform. Please use a legitimate email provider or contact an administrator for access.' } };
      }

      console.log('Email authorized:', sanitizedEmail, isAllowed ? '(allowlist)' : '(legitimate domain)');
    } catch (e) {
      console.error('Email verification failed:', e);
      return { error: { message: 'Unable to verify email access. Please try again.' } };
    }

    // Use enhanced password validation with leaked password protection
    let passwordValidation;
    try {
      const { data, error: validationError } = await supabase.rpc('validate_password_strength_enhanced', {
        password: password
      });
      
      if (validationError) {
        console.warn('Enhanced password validation failed, using fallback:', validationError);
        passwordValidation = validatePassword(password);
      } else {
        passwordValidation = data;
      }
    } catch (e) {
      console.warn('Password validation error, using fallback:', e);
      passwordValidation = validatePassword(password);
    }

    if (!passwordValidation?.valid) {
      const firstIssue = passwordValidation?.issues?.[0] || passwordValidation?.message;
      
      // Special handling for leaked passwords
      if (passwordValidation?.is_leaked) {
        return { error: { message: 'This password has been found in data breaches and is not secure. Please choose a different password.' } };
      }
      
      return { error: { message: firstIssue || 'Password does not meet security requirements' } };
    }

    // Log password strength for enhanced security monitoring (without the actual password)
    try {
      await supabase.rpc('log_security_event_enhanced', {
        event_type: 'password_validation_success',
        event_data: { 
          strength_score: passwordValidation.strength_score,
          entropy_estimate: passwordValidation.entropy_estimate
        },
        severity: 'info'
      });
    } catch (e) {
      console.warn('Failed to log enhanced password validation event:', e);
    }

    // Check rate limiting
    const rateLimitResult = checkRateLimit(sanitizedEmail);
    if (!rateLimitResult.allowed) {
      const message = rateLimitResult.reason === 'rate_limited' 
        ? `Too many attempts. Try again after ${new Date(rateLimitResult.locked_until!).toLocaleTimeString()}`
        : 'Too many attempts. Please try again later.';
      return { error: { message } };
    }

    try {
      // Determine correct confirmation redirect for current environment
      const origin = window.location.origin;
      const confirmationUrl = `${origin}/dashboard`;
      
      console.log('=== SIGNUP WITH NOTIFICATIONAPI EMAIL ONLY ===');
      console.log('Email:', sanitizedEmail);
      console.log('Confirmation URL:', confirmationUrl);
      
      // Sign up with Supabase WITHOUT sending their built-in email
      const { error: signupError } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          emailRedirectTo: confirmationUrl,
          data: {
            full_name: fullName?.trim() || ''
          }
        }
      });

      if (signupError) {
        setAuthAttempts(prev => prev + 1);
        setLastAttemptTime(Date.now());
        console.log('Supabase signup error:', signupError);
        
        // Handle specific error cases with user-friendly messages
        if (signupError.message.includes('User already registered') || signupError.message.includes('already registered')) {
          toast.error('This email is already registered. Please try logging in instead.');
          return { error: { message: 'Email already registered', type: 'user_exists' } };
        }
        
        if (signupError.message.includes('rate limit')) {
          toast.error('Too many signup attempts. Please wait before trying again.');
        } else {
          toast.error('Signup failed. Please try again.');
        }
        
        return { error: signupError };
      }

      // Send custom signup email via NotificationAPI using current environment URL
      try {
        const emailResult = await sendFailoverSignupEmail(
          sanitizedEmail,
          confirmationUrl,
          fullName?.trim() || 'User'
        );
        console.log('NotificationAPI signup result:', emailResult);
        
        if (!emailResult.success) {
          console.warn('Custom signup email failed:', emailResult.error);
          // Don't fail signup if email fails, but warn user
          toast.error('Account created but confirmation email failed. Please contact support.');
        }
      } catch (emailError) {
        // Don't fail the whole signup if custom email fails
        console.warn('Custom signup email failed but user was created:', emailError);
        toast.error('Account created but confirmation email failed. Please contact support.');
      }

      setAuthAttempts(0);
      setLastAttemptTime(0);
      console.log('Signup successful - confirmation email sent via NotificationAPI');
      return { error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { error: { message: 'Signup failed. Please try again.' } };
    }
  };

  const sendPasswordReset = async (email: string) => {
    console.log('=== CUSTOM PASSWORD RESET FUNCTION CALLED ===');
    console.log('Email:', email);
    
    const sanitizedEmail = email.toLowerCase().trim();
    
    if (!validateEmail(sanitizedEmail)) {
      console.log('Invalid email format');
      return { error: { message: 'Invalid email format' } };
    }

    const rateLimitResult = checkRateLimit(sanitizedEmail);
    if (!rateLimitResult.allowed) {
      const message = rateLimitResult.reason === 'rate_limited' 
        ? `Too many attempts. Try again after ${new Date(rateLimitResult.locked_until!).toLocaleTimeString()}`
        : 'Too many attempts. Please try again later.';
      console.log('Rate limit exceeded:', message);
      return { error: { message } };
    }

    try {
      console.log('=== CALLING PASSWORD RESET FUNCTION ===');
      
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: sanitizedEmail }
      });

      if (error) {
        console.error('❌ Password reset function failed:', error);
        return { error: { message: 'Failed to send password reset email. Please try again.' } };
      }

      if (!data?.success) {
        console.error('❌ Password reset function returned failure:', data);
        return { error: { message: data?.error || 'Failed to send password reset email. Please try again.' } };
      }

      console.log('✅ Custom password reset successful');
      setAuthAttempts(0);
      setLastAttemptTime(0);
      return { error: null };

    } catch (error) {
      console.error('❌ Password reset failed:', error);
      return { error: { message: 'Failed to send password reset email. Please try again.' } };
    }
  };

  const secureSignIn = async (email: string, password: string) => {
    // Input sanitization and validation
    const sanitizedEmail = email.toLowerCase().trim();
    
    if (!validateEmail(sanitizedEmail)) {
      return { error: { message: 'Invalid email format' } };
    }

    if (!password || password.length < 1) {
      return { error: { message: 'Password is required' } };
    }

    // Check if email is in the allowlist OR is a legitimate domain
    try {
      const { data: isAllowed, error: allowlistError } = await supabase.rpc('is_email_allowed', {
        check_email: sanitizedEmail
      });

      if (allowlistError) {
        console.error('Error checking email allowlist:', allowlistError);
      }

      // List of legitimate email providers (same as Auth.tsx)
      const legitimateEmailDomains = [
        'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
        'aol.com', 'protonmail.com', 'mail.com', 'zoho.com', 'yandex.com',
        'live.com', 'msn.com', 'rediffmail.com', 'tutanota.com', 'fastmail.com',
        'gmx.com', 'mail.ru', 'qq.com', '163.com', 'sina.com'
      ];

      const domain = sanitizedEmail.split('@')[1]?.toLowerCase();
      const isLegitimate = legitimateEmailDomains.includes(domain || '');

      // Allow if email is on allowlist OR domain is legitimate
      if (!isAllowed && !isLegitimate) {
        console.log('Email not authorized:', sanitizedEmail);
        return { error: { message: 'This email address is not authorized to access the platform. Please use a legitimate email provider or contact an administrator for access.' } };
      }

      console.log('Email authorized:', sanitizedEmail, isAllowed ? '(allowlist)' : '(legitimate domain)');
    } catch (e) {
      console.error('Email verification failed:', e);
      return { error: { message: 'Unable to verify email access. Please try again.' } };
    }

    // Check rate limiting
    const rateLimitResult = checkRateLimit(sanitizedEmail);
    if (!rateLimitResult.allowed) {
      const message = rateLimitResult.reason === 'rate_limited' 
        ? `Too many attempts. Try again after ${new Date(rateLimitResult.locked_until!).toLocaleTimeString()}`
        : 'Too many attempts. Please try again later.';
      return { error: { message } };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
      });

      if (error) {
        setAuthAttempts(prev => prev + 1);
        setLastAttemptTime(Date.now());
        
        // Log security event for failed login with enhanced monitoring
        try {
          await supabase.rpc('log_security_event_enhanced', {
            event_type: 'failed_login',
            event_data: { 
              email: sanitizedEmail, 
              error: error.message,
              attempt_number: authAttempts + 1
            },
            severity: authAttempts >= 3 ? 'high' : 'medium'
          });
        } catch (e) {
          console.warn('Failed to log enhanced security event:', e);
        }
        
        if (error.message.includes('rate limit')) {
          toast.error('Too many login attempts. Please wait before trying again.');
        }
      } else {
        setAuthAttempts(0);
        setLastAttemptTime(0);
        
        // Log successful login with enhanced monitoring
        try {
          await supabase.rpc('log_security_event_enhanced', {
            event_type: 'successful_login',
            event_data: { 
              email: sanitizedEmail,
              session_info: 'authenticated'
            },
            severity: 'info'
          });
        } catch (e) {
          console.warn('Failed to log enhanced security event:', e);
        }
      }

      return { error };
    } catch (error) {
      console.error('Signin error:', error);
      return { error: { message: 'Login failed. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      // Prevent multiple simultaneous signout attempts
      if (!session || !user) {
        console.log('Already signed out or no session');
        return;
      }
      
      // Clear local state immediately to prevent multiple calls
      setUser(null);
      setSession(null);
      setAuthAttempts(0);
      setLastAttemptTime(0);
      
      // Then sign out from Supabase
      await supabase.auth.signOut();
      console.log('Successfully signed out');
      
    } catch (error: any) {
      console.error('Signout error:', error);
      
      // If session not found or already signed out, don't show error
      if (error?.message?.includes('session') || error?.status === 403) {
        console.log('Session already expired or invalid, clearing local state');
        return;
      }
      
      // Re-establish local state if signout failed
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        setSession(currentSession);
        setUser(currentSession.user);
      }
      
      toast.error('Error signing out');
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          setAuthAttempts(0);
          setLastAttemptTime(0);
          
          setTimeout(() => {
            ensureUserProfile(session.user.id, session.user.email ?? undefined, (session.user.user_metadata as any)?.full_name);
            resetMonthlyCredits(session.user.id);
          }, 0);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        setTimeout(() => {
          ensureUserProfile(session.user.id, session.user.email ?? undefined, (session.user.user_metadata as any)?.full_name);
          resetMonthlyCredits(session.user.id);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    user,
    session,
    loading,
    authAttempts,
    signUp: secureSignUp,
    signIn: secureSignIn,
    signOut,
    sendPasswordReset,
  };
};
